import React, { useEffect, useState } from 'react';

import { useParams, useNavigate } from 'react-router-dom';
import DonationComponent from '../parts/DonationComponent';
import updateMetaTags from '../../utils/seo';
import { extractIdFromSlug } from '../../utils/urlUtils';
import { trackArticleView } from '../../utils/analytics';
import Markdown from 'react-markdown'
import './ArticlePage.css'; // Importazione del file CSS specifico
import RelatedArticles from '../parts/RelatedArticles';
import {Helmet} from "react-helmet-async";
import {useTranslation} from "react-i18next";
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

function ArticlePage(props) {
  const { slugId } = useParams();
  const navigate = useNavigate();
  const currentLang = props.language
  const { t, i18n } = useTranslation(); // Inizializza useTranslation
  // Estrai l'ID reale dallo slugId (che è nel formato "1234567890-titolo-articolo")
  const id = extractIdFromSlug(slugId);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [articleCharacters, setArticleCharacters] = useState([]);
  const [isContextOpen, setIsContextOpen] = useState(true);

  // Navigation functions for filters
  const navigateToCategory = (category) => {
    const path = `/${currentLang}/articles`;
    navigate(path, { state: { selectedCategories: [category] } });
  };

  const navigateToLocation = (location) => {
    const path = `/${currentLang}/articles`;
    navigate(path, { state: { selectedLocations: [location] } });
  };

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        let dataPath = `/data/json/article/${currentLang}/${id}/data.json`;
        const response = await fetch(dataPath);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setArticle(data);

        // Fetch characters data
        if (data.interviewee && data.interviewee.length > 0) {
          const charactersResponse = await fetch(`/data/characters_${currentLang}.json`);
          if (charactersResponse.ok) {
            const charactersData = await charactersResponse.json();
            // Filter characters that appear in the article
            const articleCharIds = data.interviewee.map(person => person.id);
            const relevantCharacters = charactersData.filter(char =>
              articleCharIds.includes(char.id));
            setArticleCharacters(relevantCharacters);
          }
        }

        // Update meta tags for SEO
        const baseUrl = window.location.origin;
        const articleUrl = `${baseUrl}/article/${id}`;
        const imageUrl = `${baseUrl}/data/json/article/img/${id}.png`;

        // Track article view for analytics (only if user consented)
        trackArticleView(id, data.editorial.title);

        // Estrai i tag e le categorie per le keyword
        const keywords = [...data.categories, ...data.hashTags.split(" ")].filter(Boolean).join(", ");

        // Crea una descrizione SEO-friendly dall'articolo
        let description = data.editorial.subtitle || '';
        if (description.length < 50 && data.interviewee && data.interviewee.length > 0) {
          description += ' ' + data.interviewee[0].why;
        }
        description = description.length > 160 ? description.substring(0, 157) + '...' : description;

        // Aggiorna tutti i meta tag
        updateMetaTags({
          title: `${data.editorial.title} | SenecaToday`,
          description: description,
          keywords: keywords,
          ogTitle: data.editorial.title,
          ogDescription: description,
          ogImage: imageUrl,
          ogUrl: articleUrl,
          twitterTitle: data.editorial.title,
          twitterDescription: description,
          twitterImage: imageUrl
        });
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();

    // Add resize listener to detect mobile/desktop view
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup resize listener
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [id]);

  if (loading) {
    return <div className="article-container">Caricamento articolo...</div>;
  }

  if (error) {
    return <div className="article-container">Errore nel caricamento dell'articolo: {error.message}</div>;
  }

  if (!article) {
    return <div className="article-container">Articolo non trovato.</div>;
  }

  // Split article content for mobile view to insert ad in the middle
  const splitArticleContent = () => {
    if (!article || !article.editorial || !article.editorial.article) return { firstHalf: '', secondHalf: '' };

    const content = article.editorial.article;
    const midPoint = Math.floor(content.length / 2);

    // Find a suitable break point (paragraph or div end) near the middle
    let breakPoint = content.indexOf('</p>', midPoint);
    if (breakPoint === -1) breakPoint = content.indexOf('</div>', midPoint);
    if (breakPoint === -1) breakPoint = midPoint;

    return {
      firstHalf: content.substring(0, breakPoint + 4), // Include the closing tag
      secondHalf: content.substring(breakPoint + 4)
    };
  };

  const { firstHalf, secondHalf } = splitArticleContent();

  // Function to get position for character sprite
  function getPosition(id) {
    const number = parseInt(id)
    const spriteWidth = 700; // Larghezza totale dello sprite
    const imageSize = 70; // Dimensione di ogni immagine (70x70)
    const imagesPerRow = spriteWidth / imageSize; // Numero di immagini per riga

    let x = ((number % imagesPerRow)===0)? (spriteWidth-70) : ((number % imagesPerRow) * imageSize)-70; // Calcola la posizione orizzontale
    const y = Math.floor((number-1) / imagesPerRow) * imageSize; // Calcola la posizione verticale

    return `-${x}px -${y}px`; // Restituisci le coordinate come stringa
  }

  // Function to replace citation placeholders [n] with clickable links
  function replaceCitationsWithLinks(text, citMap) {
    if (!text || !citMap) return text;

    // Replace [n] patterns with clickable links
    return text.replace(/\[(\d+)]/g, (match, num) => {
      const url = citMap[num];
      if (url) {
        // Remove extra quotes if present in the URL
        const cleanUrl = url.replace(/^"|"$/g, '');
        return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="citation-link">[${num}]</a>`;
      }
      return match; // Return original if no mapping found
    });
  }

  // Function to format year string
  function getYearString(year) {
    return (year < 0) ? `${Math.abs(year)} ac` : `${year} dc`;
  }

    function toDate(timeInMillis){
        return new Date(parseInt(timeInMillis));
    }

  // Function to format article date from id (timestamp)
  function formatArticleDate(id) {
    const date = new Date(parseInt(id));
    return date.toLocaleDateString(currentLang, {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }

  return (
      <>
          <Helmet>
              <script type="application/ld+json">
                  {JSON.stringify({
                      "@context": "https://schema.org",
                      "@type": "NewsArticle",
                      "headline": article.editorial.title,
                      "description": article.editorial.subtitle || '',
                      "image": [
                          `${window.location.origin}/data/json/article/img/${id}.png`
                      ],
                      "datePublished": toDate(article.id),
                      "dateModified": toDate(article.id),
                      "author": {
                          "@type": "Organization",
                          "name": "SenecaToday"
                      },
                      "publisher": {
                          "@type": "Organization",
                          "name": "SenecaToday",
                          "logo": {
                              "@type": "ImageObject",
                              "url": `${window.location.origin}/logoBlack.png`
                          }
                      },
                      "mainEntityOfPage": {
                          "@type": "WebPage",
                          "@id": `${window.location.origin}/article/${slugId}`
                      }
                  })}
              </script>
          </Helmet>

          <main>
    <div className="article-page-wrapper" style={{
      display: 'flex',
      justifyContent: 'space-between',
      flexDirection: isMobile ? 'column' : 'row'
    }}>
      {/* Left side ad - only visible on desktop */}
      {!isMobile && (
        <aside className="article-left-ad" style={{ width: '160px', position: 'sticky', top: '20px', alignSelf: 'flex-start' }}>
          <DonationComponent
            slot="1234567890" // Replace with your actual ad slot ID
            format="vertical"
            responsive={false}
            style={{ height: '600px', width: '160px' }}
          />
        </aside>
      )}

      {/* Main article content */}
      <article className="ArticleMainContent layout-content-container flex flex-col max-w-[960px] flex-1" >
        <div className="p-4">

        {/* Breadcrumb and Date row */}
        <div className="article-meta-row">
            <nav className="article-breadcrumb">
                {article.location && article.location.length > 0 && (
                    <span
                        className="breadcrumb-item clickable"
                        onClick={() => navigateToLocation(article.location[0])}
                        title={`Filtra per ${article.location.join(', ')}`}
                    >
                        {article.location.join(', ')}
                    </span>
                )}
                {article.location && article.location.length > 0 && article.mainCategory && (
                    <span className="breadcrumb-separator">&gt;</span>
                )}
                {article.mainCategory && (
                    <span
                        className="breadcrumb-item clickable"
                        onClick={() => navigateToCategory(article.mainCategory)}
                        title={`Filtra per ${article.mainCategory}`}
                    >
                        {article.mainCategory}
                    </span>
                )}
            </nav>
            <time className="article-date" dateTime={new Date(parseInt(article.id)).toISOString()}>
                {formatArticleDate(article.id)}
            </time>
        </div>

          <section className="article-header" >
            <h1 className="article-title">{article.editorial.title}</h1>
            <p className="article-subtitle-message mx-3 text-sm font-normal leading-normal">
              Un dialogo immaginario con <span>
              {article.type === "single" && article.interviewee[0].name}
              {article.type === "comparison" && (article.interviewee[0].name +' ' + t('general.e') +' ' + article.interviewee[1].name)}
            </span> per approfondire un tema di attualità.
            </p>
          </section>


          <div className="article-subtitle-setup">
            {article.interviewee.map((line, index) =>
                <div key={index}>
                  <div className="article-setup-title">{t('articlePage.why')} <span
                      className="interviewee-name">{line.name}</span></div>
                  <div className="interviewee-why"><Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw, rehypeSanitize]}>{line.why}</Markdown> </div>
                </div>
            )}
          </div>


          <section className="py-20 lg:py-8" style={{
            // border: "1px solid rgba(129, 140, 248, 0.1)",
            // borderRadius: "20px",
            // background: "linear-gradient(to right, transparent, transparent, rgba(129, 140, 248, 0.1))",
            // boxShadow: "0 4px 30px rgba(0, 0, 0, 0.2)",
            // backgroundColor: "#112027"
          }}>
            <h2 className="article-sub-title">{article.editorial.subtitle}</h2>
          </section>

        </div>

        <div className="article-main-flex">
            {(
                <figure className="article-images-right">
                    <img
                        src={`/data/json/article/img/${id}.png`}
                        alt={article.editorial.title}
                        className="article-cover-image-full"
                        loading="lazy"
                    />
                </figure>
            )}



          <div className="article-general-container">

            <section className="article-chat">
              {article.conversation.map((line, index) =>
                  <div key={index} className={"chat-"+line.role}>
                  <div className="chat-role">{line.name}</div>
                  <div className="chat-bubble">
                    <div className="chat-content">
                      <Markdown>{line.content}</Markdown>
                    </div>
                  </div>
                </div>
              )}
            </section>

            {isMobile ? (
              // Mobile layout with ad in the middle
              <section className="article-content-mobile">
                <div className="article-content-first-half" dangerouslySetInnerHTML={{ __html: firstHalf }} />

                {/* In-article ad for mobile view */}
                <div className="article-mobile-ad" style={{ margin: '20px 0' }}>
                  <DonationComponent
                    slot="3456789012" // Replace with your actual ad slot ID for in-article ads
                    format="rectangle"
                    responsive={true}
                    style={{ width: '100%', maxWidth: '336px', height: '280px', margin: '0 auto' }}
                  />
                </div>

                <div className="article-content-second-half" dangerouslySetInnerHTML={{ __html: secondHalf }} />
              </section>
            ) : (
              // Desktop layout with full article
                <section className="article-content"><Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw, rehypeSanitize]}>{article.editorial.article}</Markdown></section>
            )}

              <div className="article-subtitle-setup context-accordion">
                  <div
                      className="article-setup-title context-accordion-header"
                      onClick={() => setIsContextOpen(!isContextOpen)}
                      style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                      <span> {t('articlePage.context')} </span>
                      <span className={`accordion-arrow ${isContextOpen ? 'open' : ''}`}>▼</span>
                  </div>
                  <div className={`topic-context context-accordion-content ${isContextOpen ? 'open' : ''}`}>
                      <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw, rehypeSanitize]}>{replaceCitationsWithLinks(article.topic_context, article.cit_map)}</Markdown>
                  </div>
              </div>

            <div className="article-meta">
                <div className="category-label">Categorie:</div>
              <div  style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {article.categories.map((category, index) => (
                    <span
                        key={index}
                        className="category-item clickable"
                        onClick={() => navigateToCategory(category)}
                        title={`Filtra per ${category}`}
                    >
                        {category}
                    </span>
                ))}
              </div>
            </div>

              {/* Article Characters Section */}
              {articleCharacters.length > 0 && (
                  <div className="article-characters">
                      {/*<h3>Personaggi nell'articolo</h3>*/}
                      {articleCharacters.map(character => {
                          let imageProfileCharacterStyle = {
                              width: "70px",
                              height: "70px",
                              display: "block",
                              background: `url('/data/sprite.png') ${getPosition(character.id)}`,
                              objectFit: "cover",
                              borderRadius: "50%",
                              border: "4px solid #fff",
                              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.1)",
                          };
                          return (
                              <div key={character.id} className="character-card-article">
                                  <div className="character-info">
                                      <div style={{width: "90px"}}>
                                          <div style={imageProfileCharacterStyle}></div>
                                      </div>
                                      <div>
                                          <h4>{character.name}</h4>
                                          <div className="monospace">
                                              {getYearString(character.birth_year)} - {getYearString(character.death_year)}
                                          </div>
                                      </div>
                                  </div>
                                  <div className="character-details">
                                      <strong>{t('articlePage.topics')}</strong>
                                      <span className="monospace">{character.themes.join(", ")} </span>
                                  </div>
                                  <div className="character-details">
                                      <br/>
                                      <div style={{ textAlign: "justify" }}
                                           dangerouslySetInnerHTML={{__html: character.summary_html}}/>
                                  </div>
                              </div>
                          );
                      })}
                  </div>
              )}

            {/* Ti potrebbe interessare anche section */}
            <RelatedArticles currentArticleId={id} />
          </div>


          {/*<aside className="article-main-left-cover">*/}
          {/*  */}
          {/*</aside>*/}
        </div>
      </article>

      {/* Right side ad - only visible on desktop */}
      {!isMobile && (
        <aside className="article-right-ad" style={{ width: '160px', position: 'sticky', top: '20px', alignSelf: 'flex-start' }}>
          <DonationComponent
            slot="9876543210" // Replace with your actual ad slot ID
            format="vertical"
            responsive={false}
            style={{ height: '600px', width: '160px' }}
          />
        </aside>
      )}
    </div>
    </main>
  </>
  );
}

export default ArticlePage;
